import { useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import useMessagingWS from './useMessagingWS';
import { fetchTotalUnreadCount, setUnreadCount } from '../store/slices/messagingSlice';
import { playNotificationSound } from '../utils/notificationSound';
import { useTrendReport } from '../context/TrendReportContext';
import { QUERY_KEYS } from './useQueryHooks';
import toast from 'react-hot-toast';

/**
 * useGlobalNotifications
 *
 * Persistent hook that:
 * 1. Manages the messaging WebSocket (chat, unread badge, trend reports)
 * 2. Listens for business events pushed by the server and reactively
 *    invalidates React Query caches — so the UI updates without any
 *    manual refresh.
 *
 * Business events handled:
 *  - new_subscriber    → invalidates subscribedUsers + batches cache
 *  - batch_updated     → invalidates batches cache
 *  - service_updated   → invalidates services cache
 *  - staff_updated     → invalidates staff cache
 *
 * NOTE: WebSockets are NOT used for creating batches or services.
 * Those are standard HTTP POST operations. The WS only handles
 * server-PUSHED events (things the server tells us happened).
 */
const useGlobalNotifications = (role = 'vendor') => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const {
    handleTrendReportStarted,
    handleTrendReportReady,
    handleTrendReportFailed,
  } = useTrendReport();

  // ── Messaging events ────────────────────────────────────────────────────────

  const handleNewMessage = useCallback((data) => {
    playNotificationSound();
    
    // Show a toast with the sender's name if available, otherwise generic
    const senderName = data?.sender?.name || data?.sender_name || 'Someone';
    toast.success(`New message from ${senderName}`, {
      icon: '💬',
      position: 'top-right',
      duration: 4000,
    });
    
    // Unread count comes via the unread_count_update event below
  }, []);

  const handleUnreadUpdate = useCallback((data) => {
    if (data && typeof data.total_unread === 'number') {
      dispatch(setUnreadCount(data.total_unread));
    }
  }, [dispatch]);

  // ── Business / Real-time data events ────────────────────────────────────────
  // These events are pushed by the Go backend when data changes server-side
  // (e.g. a trainee subscribes from the customer app).
  // We respond by invalidating the relevant React Query cache so the page
  // silently re-fetches fresh data in the background.

  const handleNewSubscriber = useCallback((data) => {
    // A trainee just subscribed to one of our batches
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscribedUsers });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.batches });
    if (data?.trainee_name) {
      toast.success(`New subscriber: ${data.trainee_name}`, { icon: '🎉' });
    }
  }, [queryClient]);

  const handleBatchUpdated = useCallback(() => {
    // A batch was updated (e.g. from another session or device)
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.batches });
  }, [queryClient]);

  const handleServiceUpdated = useCallback(() => {
    // A service was updated
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.services });
  }, [queryClient]);

  const handleStaffUpdated = useCallback(() => {
    // Staff data changed
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.staff });
  }, [queryClient]);

  // ── WebSocket connection ─────────────────────────────────────────────────────

  useMessagingWS(role, {
    onNewMessage: handleNewMessage,
    onUnreadUpdate: handleUnreadUpdate,
    onTrendReportStarted: handleTrendReportStarted,
    onTrendReportReady: handleTrendReportReady,
    onTrendReportFailed: handleTrendReportFailed,
    // Business events
    onNewSubscriber: handleNewSubscriber,
    onBatchUpdated: handleBatchUpdated,
    onServiceUpdated: handleServiceUpdated,
    onStaffUpdated: handleStaffUpdated,
  });

  // Initial fetch of unread count
  useEffect(() => {
    dispatch(fetchTotalUnreadCount(role));
  }, [dispatch, role]);

  return null;
};

export default useGlobalNotifications;
